class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const productIds = this.dataset.productIds || this.dataset.productId;
    this.initializeRecommendations(productIds);
  }

  initializeRecommendations(productIds) {
    if (!productIds) return;
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productIds);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productIds) {
    const ids = productIds.split(',').map(id => id.trim()).filter(id => id);
    if (ids.length === 0) return;

    // Calculate maximum total recommendations and limit per product call (total max 20)
    const urlObj = new URL(this.dataset.url, window.location.origin);
    const originalLimit = parseInt(urlObj.searchParams.get('limit')) || 10;
    const maxTotal = ids.length > 1 ? 20 : originalLimit;
    const limitPerProduct = ids.length > 1 ? Math.max(1, Math.floor(maxTotal / ids.length)) : originalLimit;

    const sectionId = this.dataset.sectionId;
    const baseUrl = this.dataset.url;
    
    // Clean up query parameters to avoid double parameters in the final fetches
    const cleanUrlObj = new URL(baseUrl, window.location.origin);
    cleanUrlObj.searchParams.delete('product_id');
    cleanUrlObj.searchParams.delete('section_id');
    cleanUrlObj.searchParams.set('limit', limitPerProduct);
    const requestBaseUrl = cleanUrlObj.pathname + cleanUrlObj.search;

    const urls = ids.map(id => `${requestBaseUrl}&product_id=${id}&section_id=${sectionId}`);

    Promise.all(urls.map(url => fetch(url).then(response => response.text())))
      .then((responses) => {
        const parser = new DOMParser();
        const cards = [];
        const uniqueProductIds = new Set();
        const cartProductIds = this.dataset.productIds ? this.dataset.productIds.split(',').map(id => id.trim()) : [];

        let innerContainerTemplate = null;

        responses.forEach((text) => {
          const doc = parser.parseFromString(text, 'text/html');
          // Find the track containing recommended cards (try both custom element tag names just in case)
          const track = doc.querySelector('.cart-recommendations__track') || doc.querySelector('.splide__list') || doc.querySelector('.grid');
          
          if (track) {
            const itemCards = track.querySelectorAll('.cart-rec-card, .splide__slide, .grid__item');
            itemCards.forEach((card) => {
              // Extract product ID (or link path if no data-product-id)
              const cardProductId = card.dataset.productId || card.getAttribute('data-product-id') || card.querySelector('[data-product-id]')?.getAttribute('data-product-id') || card.querySelector('a')?.getAttribute('href') || card.getAttribute('href');
              if (!cardProductId) return;

              // Filter out products already in the cart
              if (cartProductIds.includes(cardProductId)) return;

              // Deduplicate recommendations
              if (uniqueProductIds.has(cardProductId)) return;

              uniqueProductIds.add(cardProductId);
              cards.push(card);
            });

            if (!innerContainerTemplate) {
              const inner = doc.querySelector('.cart-recommendations__inner') || doc.querySelector('cart-drawer-recommendations') || doc.querySelector('product-recommendations');
              if (inner) {
                innerContainerTemplate = inner.cloneNode(true);
              }
            }
          }
        });

        // Limit the total output recommendations
        const finalCards = cards.slice(0, maxTotal);

        if (finalCards.length > 0 && innerContainerTemplate) {
          const track = innerContainerTemplate.querySelector('.cart-recommendations__track') || innerContainerTemplate.querySelector('.splide__list') || innerContainerTemplate.querySelector('.grid');
          if (track) {
            track.innerHTML = '';
            finalCards.forEach(card => track.appendChild(card));
          }
          this.innerHTML = innerContainerTemplate.innerHTML;
          this.classList.add('product-recommendations--loaded');
        } else {
          // Fallback if we only had a single request or did not get matching templates
          if (responses.length === 1) {
            const html = document.createElement('div');
            html.innerHTML = responses[0];
            const recommendations = html.querySelector('cart-drawer-recommendations') || html.querySelector('product-recommendations');
            if (recommendations?.innerHTML.trim().length) {
              this.innerHTML = recommendations.innerHTML;
            }
            if (html.querySelector('.grid__item') || html.querySelector('.splide__slide') || html.querySelector('.cart-rec-card')) {
              this.classList.add('product-recommendations--loaded');
            }
          } else {
            // Hide element if no recommendations found
            this.style.display = 'none';
          }
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
